/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import { ID } from '@gh/common-shared-data';
import {
  AbstractRestService,
  arrayOf,
  identity,
  optimisticLockingOf,
  RequestMethod,
  RestSettings,
  SlicedDataSource,
} from '@gh/core-data';
import { Observable } from 'rxjs';
import {
  QuotationApproveDto,
  QuotationCloneRequestDto,
  QuotationCloneVariationsDto,
  QuotationDetailsInitResponseDto,
  QuotationDetailsSaveRequestDto,
  QuotationRfqNumberDto,
  QuotationsGetResponseDto,
  QuotationVariationsIdsGetResponseDto,
  QuoteStructureRequestDto,
  QuoteStructureResponseDto,
} from './quotation.dto';
import {
  QuotationListFilterMapper,
  QuotationListItemMapper,
  QuotationRfqNumberMapper,
  QuotationsGetResponseMapper,
  QuoteDetailsInitReponseMapper,
  QuoteDetailsSaveRequestMapper,
  QuoteStructureRequestMapper,
  QuoteStructureResponseMapper,
} from './quotation.mapper';

@Injectable()
export class QuotationRestService extends AbstractRestService {
  constructor(settings: RestSettings) {
    super(settings);
  }

  getInit(sfId: ID): Observable<QuotationDetailsInitResponseDto> {
    return this.httpGet(
      `${this.bp}/leads/sales-folders/${sfId}/quotations/init`,
      optimisticLockingOf(QuoteDetailsInitReponseMapper));
  }

  get(sfId: ID, id: ID): Observable<QuotationDetailsInitResponseDto> {
    return this.httpGet(
      `${this.crm}/leads/sales-folders/${sfId}/quotations/${id}`,
      optimisticLockingOf(QuoteDetailsInitReponseMapper));
  }

  create(quote: QuotationDetailsSaveRequestDto): Observable<QuotationDetailsInitResponseDto> {
    return this.httpPost(
      `${this.bp}/leads/sales-folders/${quote.salesFolderId}/quotations`,
      quote,
      optimisticLockingOf(QuoteDetailsSaveRequestMapper),
      optimisticLockingOf(QuoteDetailsInitReponseMapper));
  }

  update(quote: QuotationDetailsSaveRequestDto): Observable<QuotationDetailsInitResponseDto> {
    return this.httpPut(
      `${this.bp}/leads/sales-folders/${quote.salesFolderId}/quotations/${quote.quotationId}`,
      quote,
      optimisticLockingOf(QuoteDetailsSaveRequestMapper),
      optimisticLockingOf(QuoteDetailsInitReponseMapper));
  }

  clone(sfId: ID, id: ID, request: QuotationCloneRequestDto): Observable<QuotationDetailsInitResponseDto> {
    return this.httpPost(
      `${this.bp}/leads/sales-folders/${sfId}/quotations/${id}/clone`,
      request,
      identity(),
      optimisticLockingOf(QuoteDetailsInitReponseMapper));
  }

  cloneFromRfq(sfId: ID, id: ID, siteId: ID, request: QuotationCloneRequestDto): Observable<any> {
    return this.httpPost(
      `${this.bp}/quotation-list/${id}/clone/${sfId}/site/${siteId}`,
      request,
      identity(),
      optimisticLockingOf(QuoteDetailsInitReponseMapper));
  }

  cloneVariations(sfId: ID, id: ID, request: QuotationCloneVariationsDto): Observable<QuotationDetailsInitResponseDto> {
    return this.httpPut(
      `${this.bp}/leads/sales-folders/${sfId}/quotations/${id}/clone-quotation-changes`,
      request,
      identity(),
      optimisticLockingOf(QuoteDetailsInitReponseMapper));
  }

  findAllBySalesFolderId(sfId: ID): Observable<QuotationsGetResponseDto> {
    return this.httpGet(`${this.crm}/leads/sales-folders/${sfId}/quotations`, QuotationsGetResponseMapper);
  }

  approve(sfId: ID, quotationId: ID, approve: QuotationApproveDto): Observable<QuotationDetailsInitResponseDto> {
    return this.httpPut(
      `${this.bp}/leads/sales-folders/${sfId}/quotations/${quotationId}/approve`,
      approve,
      identity(),
      optimisticLockingOf(QuoteDetailsInitReponseMapper));
  }

  generateVariationIds(sfId: ID,
                       quotationId: ID,
                       componentsAmount: number,
                       itemsAmount: number): Observable<QuotationVariationsIdsGetResponseDto> {
    return this.httpGet(
      `${this.crm}/leads/sales-folders/${sfId}/quotations/${quotationId}/variations-ids`,
      {itemsAmount, componentsAmount},
      identity(),
      identity());
  }

  generateVariationIdsHlp(hlpID: ID,
    componentsAmount: number,
    itemsAmount: number): Observable<QuotationVariationsIdsGetResponseDto> {
    return this.httpGet(
      `${this.bp}/hlp/${hlpID}/variations-ids`,
      {itemsAmount, componentsAmount},
      identity(),
      identity());
  }
  getQuoteStructure(sfId: ID, quoteId: ID): Observable<QuoteStructureResponseDto> {
    return this.httpGet(
      `${this.crm}/leads/sales-folders/${sfId}/quotations/${quoteId}/rfq-structure`,
      QuoteStructureResponseMapper);
  }

  saveQuoteStructure(sfId: ID,
                     quoteId: ID,
                     structure: QuoteStructureRequestDto): Observable<QuoteStructureResponseDto> {
    return this.httpPut(
      `${this.bp}/leads/sales-folders/${sfId}/quotations/${quoteId}/rfq-structure`,
      structure,
      QuoteStructureRequestMapper,
      QuoteStructureResponseMapper);
  }

  refreshQuoteStructure(quoteId: ID): Observable<QuoteStructureResponseDto> {
    return this.httpGet(
      `${this.crm}/leads/refresh-quote-structure/${quoteId}`,
      QuoteStructureResponseMapper);
  }

  findAllAsDataSource(filter: any): SlicedDataSource<any> {
    return this.createSlicedDataSource({
      method: RequestMethod.Get,
      url: `${this.crm}/quotation`,
      body: filter,
      requestMapper: QuotationListFilterMapper,
      responseMapper: arrayOf(QuotationListItemMapper),
    });
  }

  findAllApprovedRfqNumbersBySalesFolderId(sfId: ID): Observable<QuotationRfqNumberDto[]> {
    return this.httpGet(`${this.crm}/leads/rfq-numbers/${sfId}`, arrayOf(QuotationRfqNumberMapper));
  }
}
